import { useQuery } from '@tanstack/react-query';
import { CreditCard, Download, Loader2, Wallet } from 'lucide-react';
import { downloadInvoicePdf, fetchPayments } from '../api/candidate';
import { formatEuro } from '../lib/format';
import dayjs from '../lib/dates';
import PayableInvoices from '../components/PayableInvoices';

export default function PaymentsView() {
  const { data, isLoading } = useQuery({
    queryKey: ['candidate', 'payments'],
    queryFn: fetchPayments,
  });

  const payments = data ?? [];
  const total = payments.reduce((acc, p) => acc + p.amount, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-slate-900">Mes paiements</h1>
        <p className="text-slate-600">
          Réglez vos factures et consultez l’historique de vos règlements.
        </p>
      </div>

      <PayableInvoices />

      {payments.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Wallet className="text-indigo-600" size={20} />
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Total réglé
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {formatEuro(total)}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h2 className="text-slate-900 mb-4">Historique</h2>
        {isLoading ? (
          <div className="flex items-center text-slate-500 text-sm py-6">
            <Loader2 className="animate-spin mr-2" size={16} /> Chargement…
          </div>
        ) : payments.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">
            Aucun paiement enregistré pour le moment.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center gap-4 py-3.5 first:pt-0">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <CreditCard className="text-slate-500" size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {p.label}
                  </div>
                  <div className="text-xs text-slate-500">
                    {dayjs(p.date).format('DD/MM/YYYY')} · {p.type}
                  </div>
                </div>
                <div className="text-sm font-semibold text-slate-900 flex-shrink-0">
                  {formatEuro(p.amount)}
                </div>
                {p.invoiceId && (
                  <button
                    type="button"
                    onClick={() =>
                      downloadInvoicePdf(
                        p.invoiceId as string,
                        p.label.replace(/^Facture\s+/i, ''),
                      )
                    }
                    title="Télécharger la facture"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg px-2.5 py-1.5 hover:bg-indigo-50 flex-shrink-0"
                  >
                    <Download size={14} />
                    Facture
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
